import React from 'react';

import cx from 'classnames';
import { useTranslation } from 'react-i18next';
import { Input, Checkbox, Flex } from 'design-system-web';
import { WatchlistSeverityLevel } from 'graphql-query-contracts';
import { MultiselectField, Label } from 'components';
import Pill from 'components/general/Pill';
import { PillColor } from 'application-types';
import { colors } from 'constants-colors';
import { formatWatchlistSeverityLevel } from '../../helpers/formatWatchlistSeverityLevel';

import styles from './WatchlistLevelInput.scss';

export type WatchlistLevelInputProps = {
  className?: string;
  severityLevel: WatchlistSeverityLevel;
  displayLevelText: string;
  pillColor?: PillColor;
  onDisplayLevelTextChange: (name: string) => void;
  isNotificationsEnabled: boolean;
  onIsNotificationsEnabledChange: (enabled: boolean) => void;
  availableTeams: Array<{ value: string; label: string }>;
  notificationTargets: string[];
  onNotificationTargetsChange: (teams: string[]) => void;
  isWatchlistDisplayed: boolean;
  onIsWatchlistDisplayedChange: (enabled: boolean) => void;
  isApprovalRequired: boolean;
  onIsApprovalRequiredChange: (enabled: boolean) => void;
  testId?: string;
  teamSelectionError?: string[];
};

export function WatchlistLevelInput({
  className,
  severityLevel,
  displayLevelText,
  pillColor = 'red',
  onDisplayLevelTextChange,
  isNotificationsEnabled,
  onIsNotificationsEnabledChange,
  availableTeams,
  notificationTargets,
  onNotificationTargetsChange,
  isWatchlistDisplayed,
  onIsWatchlistDisplayedChange,
  isApprovalRequired,
  onIsApprovalRequiredChange,
  testId,
  teamSelectionError,
}: WatchlistLevelInputProps): React.ReactElement {
  const { t } = useTranslation();

  const multiselectItems = availableTeams.map(team => ({
    value: team.value,
    label: team.label,
  }));

  const multiselectValue = availableTeams.filter(team =>
    notificationTargets.includes(team.value)
  );

  const getIconColor = (pillColor: PillColor) => {
    switch (pillColor) {
      case 'red':
        return colors.errorDark;
      case 'honey':
        return colors.colorYellowSecondary;
      case 'gray':
        return colors.colorGraySecondary;
      default:
        return colors.black;
    }
  };


  return (
    <Flex
      direction="column"
      gap={3}
      className={cx(styles.watchlistLevelInput, className)}
      data-test={testId}
    >
      <Flex>
        <Pill
          text={formatWatchlistSeverityLevel(severityLevel, t)}
          color={pillColor}
          size="medium"
          iconName="circle"
          iconColor={getIconColor(pillColor)}
        />
      </Flex>
      <Flex direction="column" gap={1}>
        <Label className={styles.label}>
          {t('web.admin.visitorManagement.configuration.watchlistLevel.name')}
        </Label>
        <Input
          value={displayLevelText}
          onChange={(value) => onDisplayLevelTextChange(value)}
          placeholder={t(
            'web.admin.visitorManagement.configuration.watchlistLevel.namePlaceholder'
          )}
          helperText={t(
            'web.admin.visitorManagement.configuration.watchlistLevel.nameHelperText'
          )}
          testId={`${testId}-name-input`}
        />
      </Flex>

      <Flex>
        <Checkbox
          value="emailNotifications"
          selected={isNotificationsEnabled}
          onChange={() => onIsNotificationsEnabledChange(!isNotificationsEnabled)}
          text={t(
            'web.admin.visitorManagement.configuration.watchlistLevel.emailNotifications'
          )}
          testId={`${testId}-email-notifications`}
        />
      </Flex>

      {isNotificationsEnabled && (
        <Flex direction="column" gap={1}>
          <Label className={styles.label}>
            {t(
              'web.admin.visitorManagement.configuration.watchlistLevel.selectTeams'
            )}
          </Label>
          <MultiselectField
            items={multiselectItems}
            value={multiselectValue}
            onChange={value => {
              const selectedValues = value.map(
                (item: { value: string; label: string }) => item.value
              );

              onNotificationTargetsChange(selectedValues);
            }}
            placeholder={t(
              'web.admin.visitorManagement.configuration.watchlistLevel.selectTeamsPlaceholder'
            )}
            truncateSelectedItems
            errors={teamSelectionError}
          />
        </Flex>
      )}

      <Flex>
        <Checkbox
          value="displayWatchlistLevel"
          selected={isWatchlistDisplayed}
          onChange={() => onIsWatchlistDisplayedChange(!isWatchlistDisplayed)}
          text={t(
            'web.admin.visitorManagement.configuration.watchlistLevel.displayWatchlistLevel'
          )}
          description={t(
            'web.admin.visitorManagement.configuration.watchlistLevel.displayWatchlistLevelHelperText'
          )}
          testId={`${testId}-display-watchlist-level`}
        />
      </Flex>

      <Flex>
        <Checkbox
          value="manualApproval"
          selected={isApprovalRequired}
          onChange={() => onIsApprovalRequiredChange(!isApprovalRequired)}
          text={t(
            'web.admin.visitorManagement.configuration.watchlistLevel.manualApproval'
          )}
          description={t(
            'web.admin.visitorManagement.configuration.watchlistLevel.manualApprovalHelperText'
          )}
          testId={`${testId}-manual-approval`}
        />
      </Flex>
    </Flex>
  );
}

